
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { format, parseISO } from "date-fns"
import { Search, UserRound } from "lucide-react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { useTrainer } from "@/contexts/trainer-context"
import { mockUsers } from "@/lib/mock-data"
import { AddAppointmentDialog } from "@/components/trainer-view/add-appointment-dialog"

export function TrainerClients() {
  const { t } = useTranslation()
  const { appointments, getTrainerAppointments } = useTrainer()
  const [searchQuery, setSearchQuery] = useState("")
  const [selectedDate, setSelectedDate] = useState(new Date())
  const [isAddAppointmentOpen, setIsAddAppointmentOpen] = useState(false)
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null)

  // Mock trainer ID
  const trainerId = "1"
  const trainerAppointments = getTrainerAppointments(trainerId)

  // Get unique client IDs from appointments
  const clientIds = [...new Set(trainerAppointments.map((appointment) => appointment.userId))]

  // Get client details
  const clients = mockUsers.filter((user) => clientIds.includes(user.id))

  // Filter clients based on search query
  const filteredClients = clients.filter(
    (client) =>
      client.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      client.email.toLowerCase().includes(searchQuery.toLowerCase()),
  )

  // Get upcoming appointments for a client
  const getUpcomingAppointments = (clientId: string) => {
    const today = new Date()
    return trainerAppointments
      .filter(
        (appointment) =>
          appointment.userId === clientId && appointment.status === "scheduled" && parseISO(appointment.date) >= today,
      )
      .sort((a, b) => {
        // Sort by date and then by start time
        const dateA = parseISO(a.date)
        const dateB = parseISO(b.date)
        if (dateA.getTime() !== dateB.getTime()) {
          return dateA.getTime() - dateB.getTime()
        }
        return a.startTime.localeCompare(b.startTime)
      })
  }

  const handleAddAppointment = (clientId: string) => {
    setSelectedClientId(clientId)
    setIsAddAppointmentOpen(true)
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">{t("trainerView.clients.title")}</h2>
      </div>

      <div className="relative">
        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          type="search"
          placeholder={t("trainerView.clients.searchPlaceholder")}
          className="pl-8"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {filteredClients.length === 0 ? (
          <div className="md:col-span-2 text-center py-8 text-muted-foreground">
            {searchQuery ? t("trainerView.clients.noSearchResults") : t("trainerView.clients.noClients")}
          </div>
        ) : (
          filteredClients.map((client) => {
            const upcomingAppointments = getUpcomingAppointments(client.id)
            const nextAppointment = upcomingAppointments[0]

            return (
              <Card key={client.id}>
                <CardHeader className="pb-2">
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle className="flex items-center">
                        <UserRound className="mr-2 h-5 w-5" />
                        {client.name}
                      </CardTitle>
                      <CardDescription>{client.email}</CardDescription>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => handleAddAppointment(client.id)}>
                      {t("trainerView.clients.schedule")}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="text-sm font-medium">{t("trainerView.clients.upcomingAppointments")}</div>

                    {upcomingAppointments.length === 0 ? (
                      <div className="text-sm text-muted-foreground">
                        {t("trainerView.clients.noUpcomingAppointments")}
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {upcomingAppointments.slice(0, 3).map((appointment) => (
                          <div
                            key={appointment.id}
                            className="flex justify-between items-center p-2 rounded-md bg-muted"
                          >
                            <div>
                              <div className="font-medium">{format(parseISO(appointment.date), "MMM d, yyyy")}</div>
                              <div className="text-sm text-muted-foreground">
                                {appointment.startTime} - {appointment.endTime}
                              </div>
                            </div>
                            <Badge>{appointment.type}</Badge>
                          </div>
                        ))}

                        {upcomingAppointments.length > 3 && (
                          <div className="text-sm text-muted-foreground text-center">
                            {t("trainerView.clients.moreAppointments", { count: upcomingAppointments.length - 3 })}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )
          })
        )}
      </div>

      {/* Add appointment dialog */}
      <AddAppointmentDialog
        open={isAddAppointmentOpen}
        onOpenChange={setIsAddAppointmentOpen}
        selectedDate={selectedDate}
        trainerId={trainerId}
      />
    </div>
  )
}
